import React from 'react';
import styles from './LiveGroupView.module.scss';
import PerformancePie from './PerformancePie';
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import { VelocityRate, AutomaticMetricsType } from '../../constants';
import type { LiveRepFrame } from '../../types/automatic';
import { useSessionData } from '../../providers';

const liveWorkoutMenuProps = {
  PaperProps: {
    sx: {
      minWidth: 180,
      maxWidth: 'min(340px, calc(100vw - 32px))',
      overflowX: 'auto',
    },
  },
  MenuListProps: {
    sx: {
      width: 'max-content',
      minWidth: '100%',
      overflowX: 'auto',
      '& .MuiMenuItem-root': {
        whiteSpace: 'nowrap',
      },
    },
  },
};

interface LiveGroupViewProps {
  liveSets: {
    userId: string;
    metrics: LiveRepFrame[];
    concentricType: any;
    eccentricType: any;
  }[];
  finishedSets: {
    userId: string;
    metrics: LiveRepFrame[];
    concentricType: any;
    eccentricType: any;
  }[];
  selectedGroupId: string;
  setSelectedGroupId: React.Dispatch<React.SetStateAction<string>>;
  selectedAthleteId: string;
  setSelectedAthleteId: React.Dispatch<React.SetStateAction<string>>;
}

function classify(
  value: number | undefined,
  min: number | undefined,
  max: number | undefined,
  type?: any,
): VelocityRate {
  if (value == null || min == null || max == null) return VelocityRate.Good;
  if (type === AutomaticMetricsType.Velocity) {
    if (value < min) return VelocityRate.Slow;
    if (value > max) return VelocityRate.Fast;
    return VelocityRate.Good;
  }
  if (value < min) return VelocityRate.Fast;
  if (value > max) return VelocityRate.Slow;
  return VelocityRate.Good;
}

const LiveGroupView: React.FC<LiveGroupViewProps> = ({
  liveSets,
  finishedSets,
  selectedGroupId,
  setSelectedGroupId,
  selectedAthleteId,
  setSelectedAthleteId,
}) => {
  const { groups, members, mergedExercisesMap } = useSessionData();

  const groupMemberSet = React.useMemo(
    () =>
      selectedGroupId !== 'all'
        ? new Set(groups[selectedGroupId]?.memberIds || [])
        : null,
    [selectedGroupId, groups],
  );

  const setFilter = React.useCallback(
    (s: { userId: string }) => {
      const byGroup = !groupMemberSet || groupMemberSet.has(s.userId);
      const byAthlete =
        selectedAthleteId === 'all' || selectedAthleteId === s.userId;
      return byGroup && byAthlete;
    },
    [groupMemberSet, selectedAthleteId],
  );

  const sets = React.useMemo(() => {
    return [...liveSets.filter(setFilter), ...finishedSets.filter(setFilter)];
  }, [liveSets, finishedSets, setFilter]);

  // Aggregate overall and per exercise (eccentric and concentric separately)
  type Counters = { good: number; fast: number; slow: number; target: number };
  const overallCon: Counters = { good: 0, fast: 0, slow: 0, target: 0 };
  const overallEcc: Counters = { good: 0, fast: 0, slow: 0, target: 0 };
  const byExerciseCon = new Map<string, Counters & { name: string }>();
  const byExerciseEcc = new Map<string, Counters & { name: string }>();

  for (const s of sets) {
    // infer exercise and target reps once per set
    const firstMetric = s.metrics[0];
    const exIdForSet = firstMetric?.exerciseId || 'unknown';
    const targetRepsForSet = firstMetric?.preset?.isAmrap
      ? 0
      : (firstMetric?.preset?.reps as number | undefined) ??
      (Array.isArray(s.metrics) ? s.metrics.length : 0);
    const exName = mergedExercisesMap[exIdForSet]?.name || 'Exercise';
    const conAcc = byExerciseCon.get(exIdForSet) || {
      good: 0,
      fast: 0,
      slow: 0,
      target: 0,
      name: exName,
    };
    const eccAcc = byExerciseEcc.get(exIdForSet) || {
      good: 0,
      fast: 0,
      slow: 0,
      target: 0,
      name: exName,
    };

    for (const m of s.metrics) {
      // concentric classification (up)
      const c = classify(
        m.upDuration,
        m.preset?.minConcentric,
        m.preset?.maxConcentric,
        m.preset?.concentricType,
      );
      if (c === VelocityRate.Good) {
        overallCon.good += 1;
        conAcc.good += 1;
      } else if (c === VelocityRate.Fast) {
        overallCon.fast += 1;
        conAcc.fast += 1;
      } else {
        overallCon.slow += 1;
        conAcc.slow += 1;
      }
      // eccentric classification (down)
      const e = classify(
        m.downDuration,
        m.preset?.minEccentric,
        m.preset?.maxEccentric,
        m.preset?.eccentricType,
      );
      if (e === VelocityRate.Good) {
        overallEcc.good += 1;
        eccAcc.good += 1;
      } else if (e === VelocityRate.Fast) {
        overallEcc.fast += 1;
        eccAcc.fast += 1;
      } else {
        overallEcc.slow += 1;
        eccAcc.slow += 1;
      }
    }

    // add targets once per set (applied to both modes)
    overallCon.target += targetRepsForSet;
    overallEcc.target += targetRepsForSet;
    conAcc.target += targetRepsForSet;
    eccAcc.target += targetRepsForSet;
    byExerciseCon.set(exIdForSet, conAcc);
    byExerciseEcc.set(exIdForSet, eccAcc);
  }

  // Dropdown sources
  const athleteOptions = React.useMemo(() => {
    const ids = new Set<string>();
    const arr: { id: string; label: string }[] = [];
    sets.forEach((set) => {
      if (ids.has(set.userId)) return;
      const member = members[set.userId];
      if (member) {
        const first = member.firstName ?? member.name?.split(' ')?.[0] ?? '';
        const last =
          member.lastName ??
          (member.name && member.name.split(' ').length > 1
            ? member.name.split(' ')[1]
            : '');
        arr.push({ id: member.id, label: `${first} ${last}`.trim() });
        ids.add(set.userId);
      }
    });
    return arr.sort((a, b) => a.label.localeCompare(b.label));
  }, [sets, members]);

  return (
    <div>
      {/* Filters above the content container */}
      <div className={styles.filtersRow}>
        <FormControl size="small" className={styles.filterControl}>
          <InputLabel className={styles.inputLabel} id="group-select-label">
            Group
          </InputLabel>
          <Select
            labelId="group-select-label"
            value={selectedGroupId}
            label="Group"
            onChange={(e) => setSelectedGroupId(e.target.value as string)}
            className={styles.select}
            autoWidth
            MenuProps={liveWorkoutMenuProps}
          >
            <MenuItem value="all">All Groups</MenuItem>
            {Object.values(groups).map((g) => (
              <MenuItem key={g.id} value={g.id}>
                {g.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" className={styles.filterControl}>
          <InputLabel className={styles.inputLabel} id="athlete-select-label">
            Athlete
          </InputLabel>
          <Select
            labelId="athlete-select-label"
            value={selectedAthleteId}
            label="Athlete"
            onChange={(e) => setSelectedAthleteId(e.target.value as string)}
            className={styles.select}
            autoWidth
            MenuProps={liveWorkoutMenuProps}
          >
            <MenuItem value="all">All Athletes</MenuItem>
            {athleteOptions.map((a) => (
              <MenuItem key={a.id} value={a.id}>
                {a.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>

      {/* Content container with background */}
      <div className={styles.groupViewContainer}>
        {/* Overall */}
        <div className={styles.tableRow}>
          <div className={styles.tableFirstColumnCell}>
            <span className={styles.title}>Group Performance</span>
            <span className={styles.subtitle} style={{ marginTop: '10px' }}>
              OVERALL (Good/Fast/Slow)
            </span>
          </div>
          <div className={styles.tablePieColumnCell}>
            <PerformancePie
              good={overallEcc.good}
              fast={overallEcc.fast}
              slow={overallEcc.slow}
              target={overallEcc.target}
              subtitle="ECCENTRIC"
            />
          </div>
          <div className={styles.tablePieColumnCell}>
            <PerformancePie
              good={overallCon.good}
              fast={overallCon.fast}
              slow={overallCon.slow}
              target={overallCon.target}
              subtitle="CONCENTRIC"
            />
          </div>
        </div>
        {/* Per Exercise */}
        <div className={styles.tableEmptyRow}>
          <div className={styles.tableFirstColumnCell}>
            <span className={styles.subtitle}>PER EXERCISE METRICS</span>
          </div>
        </div>
        {[...new Set([...byExerciseCon.keys(), ...byExerciseEcc.keys()])].map(
          (exId) => {
            const con = byExerciseCon.get(exId) || {
              good: 0,
              fast: 0,
              slow: 0,
              target: 0,
              name: mergedExercisesMap[exId]?.name || 'Exercise',
            };
            const ecc = byExerciseEcc.get(exId) || {
              good: 0,
              fast: 0,
              slow: 0,
              target: 0,
              name: mergedExercisesMap[exId]?.name || 'Exercise',
            };
            const name = con.name || ecc.name;
            return (
              <div className={styles.tableRow} key={exId}>
                <div className={styles.tableFirstColumnCell}>
                  <span className={styles.title}>{name}</span>
                </div>
                <div className={styles.tablePieColumnCell}>
                  <PerformancePie
                    good={ecc.good}
                    fast={ecc.fast}
                    slow={ecc.slow}
                    target={ecc.target}
                    subtitle="ECCENTRIC"
                  />
                </div>
                <div className={styles.tablePieColumnCell}>
                  <PerformancePie
                    good={con.good}
                    fast={con.fast}
                    slow={con.slow}
                    target={con.target}
                    subtitle="CONCENTRIC"
                  />
                </div>
              </div>
            );
          },
        )}
      </div>
    </div>
  );
};

export default LiveGroupView;
